// run-header — default skin module
// Token disposition: pass-through (no overrides)
// NO external imports — plain JavaScript only

export const styles = `
  :host {
    display: block;
    position: sticky;
    top: 0;
    z-index: 100;
    height: var(--run-header-height, 3.5rem);
    background: var(--run-header-bg, var(--run-color-surface-raised));
    color: var(--run-header-color, var(--run-color-text-primary));
    border-bottom: var(--run-header-border-width, 1px) solid var(--run-header-border, var(--run-color-border-default));
    box-shadow: var(--run-header-shadow, var(--run-elevation-semantic-overlay));
    font-size: var(--run-header-font-size, var(--run-font-size-base));
    font-weight: var(--run-header-font-weight, var(--run-font-semantic-weight-medium));
    font-family: var(--run-header-font-family, var(--run-font-semantic-family-body));
  }

  .header-inner {
    display: flex;
    align-items: center;
    gap: var(--run-header-gap, var(--run-space-semantic-xs));
    max-width: var(--run-header-max-width, 87.5rem);
    height: 100%;
    margin-inline: auto;
    padding-inline: var(--run-header-gutter, var(--run-space-semantic-sm));
  }

  .header-start {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  .header-nav {
    display: flex;
    align-items: center;
    flex: 1;
    gap: 0;
  }

  .header-end {
    display: flex;
    align-items: center;
    gap: var(--run-header-gap, var(--run-space-semantic-xs));
    flex-shrink: 0;
    margin-inline-start: auto;
  }

  /* Default nav link slot content */
  ::slotted([data-key]) {
    color: var(--run-header-nav-link-color, var(--run-color-text-secondary));
    transition:
      color var(--run-motion-semantic-micro-duration) var(--run-motion-semantic-standard-easing),
      background var(--run-motion-semantic-micro-duration) var(--run-motion-semantic-standard-easing);
  }

  .nav-link {
    display: inline-flex;
    align-items: center;
    padding-block: var(--run-header-padding-block, var(--run-space-semantic-xs));
    padding-inline: var(--run-header-gutter, var(--run-space-semantic-sm));
    color: var(--run-header-nav-link-color, var(--run-color-text-secondary));
    text-decoration: none;
    border-radius: var(--run-header-radius, 0);
    transition:
      color var(--run-motion-semantic-micro-duration) var(--run-motion-semantic-standard-easing),
      background var(--run-motion-semantic-micro-duration) var(--run-motion-semantic-standard-easing);
  }

  .nav-link:hover {
    color: var(--run-header-nav-link-active-color, var(--run-color-text-primary));
    background: var(--run-header-background-hover, var(--run-color-state-hover-overlay));
  }

  .nav-link:focus-visible {
    outline: 2px solid var(--run-color-border-default);
    outline-offset: 2px;
    transition:
      outline-color var(--run-motion-semantic-micro-duration) var(--run-motion-semantic-standard-easing);
  }

  .nav-link[aria-current="page"] {
    color: var(--run-header-nav-link-active-color, var(--run-color-text-primary));
    background: var(--run-header-nav-link-active-bg, var(--run-color-intent-primary-subtle));
    font-weight: var(--run-font-semantic-weight-medium);
    transition:
      color var(--run-motion-semantic-standard-duration) var(--run-motion-semantic-standard-easing),
      background var(--run-motion-semantic-standard-duration) var(--run-motion-semantic-standard-easing);
  }

  .search-trigger {
    display: inline-flex;
    align-items: center;
    gap: var(--run-space-semantic-xs);
    padding-block: var(--run-header-padding-block, var(--run-space-semantic-xs));
    padding-inline: var(--run-header-gutter, var(--run-space-semantic-sm));
    background: transparent;
    border: 1px solid var(--run-header-border, var(--run-color-border-default));
    border-radius: var(--run-header-radius, 0);
    color: var(--run-header-color-muted, var(--run-color-text-secondary));
    font-size: var(--run-header-font-size, var(--run-font-size-base));
    cursor: pointer;
    transition:
      background var(--run-motion-semantic-micro-duration) var(--run-motion-semantic-standard-easing),
      color var(--run-motion-semantic-micro-duration) var(--run-motion-semantic-standard-easing);
  }

  .search-trigger:hover {
    background: var(--run-header-background-hover, var(--run-color-state-hover-overlay));
    color: var(--run-header-color, var(--run-color-text-primary));
  }

  .search-trigger:focus-visible {
    outline: 2px solid var(--run-color-border-default);
    outline-offset: 2px;
    transition:
      outline-color var(--run-motion-semantic-micro-duration) var(--run-motion-semantic-standard-easing);
  }

  .search-trigger:active {
    opacity: 0.8;
  }

  .theme-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    background: transparent;
    border: none;
    border-radius: var(--run-header-radius, 0);
    color: var(--run-header-color-muted, var(--run-color-text-secondary));
    cursor: pointer;
    transition:
      background var(--run-motion-semantic-micro-duration) var(--run-motion-semantic-standard-easing),
      color var(--run-motion-semantic-micro-duration) var(--run-motion-semantic-standard-easing);
  }

  .theme-toggle:hover {
    background: var(--run-header-background-hover, var(--run-color-state-hover-overlay));
    color: var(--run-header-color, var(--run-color-text-primary));
  }

  .theme-toggle:focus-visible {
    outline: 2px solid var(--run-color-border-default);
    outline-offset: 2px;
    transition:
      outline-color var(--run-motion-semantic-micro-duration) var(--run-motion-semantic-standard-easing);
  }

  .theme-toggle:active {
    opacity: 0.8;
  }

  .logo-link {
    display: inline-flex;
    align-items: center;
    gap: var(--run-space-semantic-xs);
    text-decoration: none;
    color: var(--run-header-color, var(--run-color-text-primary));
  }

  .logo-link:focus-visible {
    outline: 2px solid var(--run-color-border-default);
    outline-offset: 2px;
  }

  .logo-badge {
    font-size: var(--run-font-size-base);
    color: var(--run-header-color-muted, var(--run-color-text-secondary));
  }

  .github-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    color: var(--run-header-color-muted, var(--run-color-text-secondary));
    text-decoration: none;
    border-radius: var(--run-header-radius, 0);
    transition: color var(--run-motion-semantic-micro-duration) var(--run-motion-semantic-standard-easing);
  }

  .github-link:hover {
    color: var(--run-header-color, var(--run-color-text-primary));
  }

  .github-link:focus-visible {
    outline: 2px solid var(--run-color-border-default);
    outline-offset: 2px;
  }

  @media (max-width: 48rem) {
    .header-nav {
      display: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .nav-link[aria-current="page"] {
      transition: none;
    }
  }
`;

const NAV_ITEMS = [
  { key: 'home',       label: 'Home',       href: '/' },
  { key: 'docs',       label: 'Docs',       href: '/docs' },
  { key: 'components', label: 'Components', href: '/components' },
  { key: 'tokens',     label: 'Tokens',     href: '/tokens' },
  { key: 'guides',     label: 'Guides',     href: '/guides' },
  { key: 'showcase',   label: 'Showcase',   href: '/showcase' },
  { key: 'changelog',  label: 'Changelog',  href: '/changelog' },
  { key: 'about',      label: 'About',      href: '/about' },
];

export function render(host) {
  const { _activeKey, theme } = host; // declared in MVA SkinModule.render contract

  const themeToggleLabel = theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme';
  const themeIcon = theme === 'light'
    ? `<svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 1.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11z"/></svg>`
    : `<svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M7.5 0a.75.75 0 0 1 .75.75v1.5a.75.75 0 0 1-1.5 0V.75A.75.75 0 0 1 7.5 0zm0 12a.75.75 0 0 1 .75.75v1.5a.75.75 0 0 1-1.5 0v-1.5A.75.75 0 0 1 7.5 12zM15 7.5a.75.75 0 0 1-.75.75h-1.5a.75.75 0 0 1 0-1.5h1.5A.75.75 0 0 1 15 7.5zM3 7.5a.75.75 0 0 1-.75.75H.75a.75.75 0 0 1 0-1.5h1.5A.75.75 0 0 1 3 7.5zM7.5 3.5A4 4 0 1 0 7.5 11.5 4 4 0 0 0 7.5 3.5z"/></svg>`;

  const navLinksHtml = NAV_ITEMS.map(({ key, label, href }) => {
    const isActive = _activeKey === key;
    return `<a
      class="nav-link"
      href="${href}"
      data-key="${key}"
      ${isActive ? 'aria-current="page"' : ''}
    >${label}</a>`;
  }).join('');

  host.shadowRoot.innerHTML = `
    <div class="header-inner">
      <div class="header-start">
        <slot name="logo">
          <a class="logo-link" href="/">
            <svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><rect width="24" height="24" rx="4"/></svg>
            <span>runsnative.org</span>
            <span class="logo-badge">v1</span>
          </a>
        </slot>
      </div>
      <nav class="header-nav" role="navigation" aria-label="Primary">
        <slot name="nav">
          ${navLinksHtml}
        </slot>
      </nav>
      <div class="header-end">
        <slot name="search-trigger">
          <button class="search-trigger" type="button" aria-label="Search (⌘K)">
            <svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="6" cy="6" r="4"/><line x1="9.5" y1="9.5" x2="13" y2="13"/></svg>
            <span>Search</span>
            <kbd>⌘K</kbd>
          </button>
        </slot>
        <slot name="actions">
          <a
            class="github-link"
            href="https://github.com/runsnative"
            target="_blank"
            rel="noreferrer"
            aria-label="GitHub"
          >
            <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0 0 16 8c0-4.42-3.58-8-8-8z"/></svg>
          </a>
          <button
            class="theme-toggle"
            type="button"
            aria-label="${themeToggleLabel}"
          >${themeIcon}</button>
        </slot>
      </div>
    </div>
  `;
}